import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  DatePicker,
  Space,
  Divider,
  Row,
  Col,
  Card,
  Typography,
} from 'antd';
import { Promotion, DiscountType } from '../../types/promotions';
import dayjs from 'dayjs';
import { showError, withNotification } from '../../utils/notificationUtils';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const datetimeFormat = 'YYYY-MM-DD HH:mm:ss';

interface PromotionFormProps {
  initialData?: Partial<Promotion>;
  onSubmit: (
    data: Omit<Promotion, 'id' | 'currentUsage' | 'createdAt' | 'updatedAt'>
  ) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const PromotionForm: React.FC<PromotionFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const [form] = Form.useForm();
  const [discountType, setDiscountType] = useState<DiscountType>(
    initialData?.discountType || 'percentage'
  );

  // Xử lý khi thay đổi loại giảm giá
  const handleDiscountTypeChange = (value: DiscountType) => {
    setDiscountType(value);

    // Reset giá trị giảm giá khi thay đổi loại
    form.setFieldsValue({ discountValue: undefined });
  };

  // Xử lý submit form
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Chuyển đổi định dạng ngày
      const [startDate, endDate] = values.timeRange || [null, null];

      // Tạo đối tượng khuyến mãi từ form values
      const promotionData: Omit<
        Promotion,
        'id' | 'currentUsage' | 'createdAt' | 'updatedAt'
      > = {
        name: values.name,
        description: values.description,
        code: values.code,
        discountType: values.discountType,
        discountValue: values.discountValue,
        minOrderValue: values.minOrderValue,
        maxDiscountValue: values.maxDiscountValue,
        startDate: startDate
          ? startDate.toISOString()
          : new Date().toISOString(),
        endDate: endDate
          ? endDate.toISOString()
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        isActive: true,
        usageLimit: values.usageLimit,
        applicableScope: 'all',
        applicableItems: [],
        exclusions: values.exclusions || [],
        imageUrl: values.imageUrl || '',
      };

      await withNotification(() => onSubmit(promotionData), {
        successMessage: initialData
          ? 'Cập nhật khuyến mãi thành công'
          : 'Tạo khuyến mãi thành công',
        errorMessage: initialData
          ? 'Cập nhật khuyến mãi thất bại'
          : 'Tạo khuyến mãi thất bại',
      });
    } catch (error) {
      console.error('Form validation failed:', error);
      showError('Lỗi', 'Vui lòng kiểm tra lại thông tin khuyến mãi');
    }
  };

  return (
    <Form
      form={form}
      layout='vertical'
      initialValues={{
        name: initialData?.name || '',
        description: initialData?.description || '',
        code: initialData?.code || '',
        discountType: initialData?.discountType || 'percentage',
        discountValue: initialData?.discountValue,
        minOrderValue: initialData?.minOrderValue,
        maxDiscountValue: initialData?.maxDiscountValue,
        timeRange:
          initialData?.startDate && initialData?.endDate
            ? [dayjs(initialData.startDate), dayjs(initialData.endDate)]
            : [dayjs(), dayjs().add(1, 'month')],
        isActive: true,
        usageLimit: initialData?.usageLimit,
        exclusions: initialData?.exclusions || [],
        imageUrl: initialData?.imageUrl || '',
      }}
    >
      <Row gutter={24}>
        <Col span={16}>
          {/* Thông tin cơ bản */}
          <Card title='Thông tin cơ bản' className='mb-4'>
            <Form.Item
              name='name'
              label='Tên khuyến mãi'
              rules={[
                { required: true, message: 'Vui lòng nhập tên khuyến mãi' },
              ]}
            >
              <Input placeholder='Nhập tên khuyến mãi' />
            </Form.Item>

            <Form.Item
              name='code'
              label='Mã khuyến mãi'
              tooltip='Mã khuyến mãi phải là chữ và số, không dấu, không khoảng trắng'
              rules={[
                { required: true, message: 'Vui lòng nhập mã khuyến mãi' },
                {
                  pattern: /^[A-Za-z0-9]+$/,
                  message:
                    'Mã khuyến mãi chỉ được chứa chữ cái và số, không dấu, không khoảng trắng',
                },
              ]}
            >
              <Input placeholder='Nhập mã khuyến mãi (VD: SUMMER2024)' />
            </Form.Item>

            <Form.Item name='description' label='Mô tả'>
              <TextArea rows={4} placeholder='Nhập mô tả khuyến mãi' />
            </Form.Item>

            <Form.Item
              name='timeRange'
              label='Thời gian áp dụng'
              rules={[
                { required: true, message: 'Vui lòng chọn thời gian áp dụng' },
              ]}
            >
              <RangePicker
                // defaultValue={[dayjs(), dayjs().add(3, 'month')]}
                showTime
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Card>

          {/* Cấu hình giảm giá */}
          <Card title='Cấu hình giảm giá' className='mb-4'>
            <Form.Item
              name='discountType'
              label='Loại giảm giá'
              rules={[
                { required: true, message: 'Vui lòng chọn loại giảm giá' },
              ]}
            >
              <Select
                onChange={(value) =>
                  handleDiscountTypeChange(value as DiscountType)
                }
              >
                <Option value='percentage'>Giảm theo phần trăm (%)</Option>
                <Option value='fixed_amount'>Giảm số tiền cố định (VND)</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='discountValue'
              label={
                discountType === 'percentage'
                  ? 'Phần trăm giảm (%)'
                  : 'Số tiền giảm (VND)'
              }
              rules={[
                { required: true, message: 'Vui lòng nhập giá trị giảm giá' },
              ]}
            >
              <InputNumber
                min={0}
                max={discountType === 'percentage' ? 100 : undefined}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name='minOrderValue'
              label='Giá trị đơn hàng tối thiểu (VND)'
              tooltip='Đơn hàng phải đạt giá trị tối thiểu này mới được áp dụng khuyến mãi'
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>

            {discountType === 'percentage' && (
              <Form.Item
                name='maxDiscountValue'
                label='Giới hạn số tiền giảm tối đa (VND)'
                tooltip='Giới hạn số tiền giảm tối đa khi áp dụng khuyến mãi theo phần trăm'
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            )}

            <Form.Item
              name='usageLimit'
              label='Giới hạn số lần sử dụng'
              tooltip='Số lần khuyến mãi có thể được sử dụng (để trống nếu không giới hạn)'
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Card>
        </Col>

        {/* Cột thông tin bổ sung bên phải */}
        <Col span={8}>
          <Card title='Ảnh đại diện' className='mb-4'>
            <Form.Item name='imageUrl' label='Đường dẫn ảnh'>
              <Input placeholder='URL hình ảnh khuyến mãi' />
            </Form.Item>
            {/* Có thể thêm chức năng upload ảnh ở đây */}
          </Card>

          <Card title='Thông tin bổ sung' className='mb-4'>
            <Text type='secondary'>
              Mã khuyến mãi sẽ được hiển thị cho khách hàng khi họ đạt điều kiện
              áp dụng. Hãy đảm bảo đặt các giới hạn hợp lý để tránh lạm dụng.
            </Text>
            <Text type='secondary' className='block mt-2'>
              Mã khuyến mãi luôn được tạo với trạng thái kích hoạt.
            </Text>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Các nút hành động */}
      <Form.Item>
        <Space>
          <Button type='primary' onClick={handleSubmit} loading={isSubmitting}>
            {initialData ? 'Cập nhật' : 'Tạo mới'}
          </Button>
          <Button onClick={onCancel}>Hủy</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default PromotionForm;
